import TableContainer from "components/Common/DataTableContainer"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import {
  cancelStallBooking,
  downloadBookingReport,
  fetchStallList,
  getPaymentStatistics,
} from "../../../redux/actions/pvs"
import { Button } from "reactstrap"
import ConfirmPopup from "components/ConfirmPopup"
import ErrorPopup from "components/ErrorPopup"
import {
  DOWNLOAD_BOOKING_REPORT_RESET,
  GET_PAYMENT_STATISTICS_RESET,
} from "../../../redux/types/pvs"
import { toast } from "react-toastify"
import PageLoader from "components/Common/PageLoader"

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const BookingReport = () => {
  document.title = "Booking Report | PULA"

  const query = useQuery()
  const combineGroupId = query.get("id")

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [paymentStatistic, setPaymentStatistic] = useState(null)

  const token = useSelector(state => state.auth.token)
  const bookingReport = useSelector(state => state.pvs.bookingReport)
  const paymentStatistics = useSelector(state => state.pvs.paymentStatistics)
  const bookingReportFileLink = useSelector(
    state => state.pvs.bookingReportFileLink
  )
  const downloadBookingReportScreen = useSelector(
    state => state.screens.pvs.downloadBookingReport
  )

  useEffect(() => {
    dispatch({ type: DOWNLOAD_BOOKING_REPORT_RESET })
    fetchReport("list")
  }, [])

  useEffect(() => {
    if (downloadBookingReportScreen.success) {
      if (bookingReportFileLink) {
        const link = document.createElement("a")
        link.href = bookingReportFileLink
        link.download = "booking-report.zip"
        link.click()
      }
      dispatch({ type: DOWNLOAD_BOOKING_REPORT_RESET })
    }
    if (downloadBookingReportScreen.error) {
      toast.error(downloadBookingReportScreen.error)
      dispatch({ type: DOWNLOAD_BOOKING_REPORT_RESET })
    }
  }, [downloadBookingReportScreen])

  useEffect(() => {
    const paymentStatistic = paymentStatistics.find(
      p => p.enc_combine_group_id === combineGroupId
    )
    setPaymentStatistic(paymentStatistic)
  }, [paymentStatistics])

  const fetchReport = type => {
    dispatch(
      downloadBookingReport(
        {
          enc_combine_group_id: combineGroupId,
          report_type: type, //list , excel
        },
        token
      )
    )
  }

  const columns = useMemo(
    () => [
      {
        header: "Seller Name",
        accessorKey: "seller_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "PVS Number",
        accessorKey: "pvs_card_number",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Products",
        accessorKey: "product_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Billing Name",
        accessorKey: "billing_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Company Name",
        accessorKey: "company_name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Billing Contact",
        accessorKey: "billing_mobile_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Stall No",
        accessorKey: "stall_numbers",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Stall Status",
        accessorKey: "stall_status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span
              className={`badge ${
                value === "Booked"
                  ? "bg-success"
                  : value === "Payment Pending" || value === "Active"
                  ? "bg-warning"
                  : "bg-danger"
              }`}
            >
              {value}
            </span>
          )
        },
      },
      {
        header: "Stall Type",
        accessorKey: "stall_type",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Two Sides",
        accessorKey: "two_sides_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Invoice Status",
        accessorKey: "invoice_status",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Invoice No",
        accessorKey: "invoice_no",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Base Amount",
        accessorKey: "base_price",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `₹ ${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "SGST",
        accessorKey: "sgst",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `₹ ${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "CGST",
        accessorKey: "cgst",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `₹ ${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Discount(%)",
        accessorKey: "discount_percent",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
      {
        header: "Total Paid",
        accessorKey: "total_amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: info => {
          const value = info.getValue()
          return (
            <span>{value ? `₹ ${value.toLocaleString("en-IN")}` : " - "}</span>
          )
        },
      },
    ],
    [navigate]
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-end mt-3 mb-3">
          <div className="flex-grow-1">
            <h4 className="h4 mb-0 nom-title">{`${paymentStatistic?.bazaar_name}`}</h4>
          </div>

          <Button
            color="primary"
            onClick={() => {
              fetchReport("excel")
            }}
          >
            Download report
          </Button>
        </div>
        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={bookingReport || []}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          buttonClass="btn btn-primary"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4"
        />
      </div>
      {/* <PageLoader open={getPaymentStatisticsScreen.loading} /> */}
    </div>
  )
}

export default BookingReport
